/**
 * Утилиты для работы с ролями пользователей EyeHub.
 *
 * Роли (см. BUILD_BRIEF.md / ADR §Auth):
 *   Провайдер (provider-ops):
 *     - hub-admin              — администратор хаба (полный доступ)
 *     - hub-support-engineer   — инженер поддержки (тикеты всех организаций)
 *   Организация:
 *     - org-admin              — администратор организации
 *     - org-member             — участник организации
 *
 * Роли приходят из Keycloak как массив строк в user.roles (kebab-case).
 * Внутри приложения нормализуем в формат [{name: 'hub-admin'}, ...] через
 * getUserGroups() — так же, как это делает шаблон Lexa (state.auth.user.roles).
 */

import { logger } from './logger';

/** Канонические имена ролей EyeHub. */
export const USER_ROLES = {
  HUB_ADMIN: 'hub-admin',
  HUB_SUPPORT_ENGINEER: 'hub-support-engineer',
  ORG_ADMIN: 'org-admin',
  ORG_MEMBER: 'org-member',
};

/** Роли провайдера (сотрудники хаба). */
export const PROVIDER_ROLES = [USER_ROLES.HUB_ADMIN, USER_ROLES.HUB_SUPPORT_ENGINEER];
/** Роли организации. */
export const ORG_ROLES = [USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_MEMBER];

/**
 * Нормализует user → массив групп формата [{name: 'hub-admin'}, ...].
 *
 * Поддерживает:
 *   - user.groups: [{name}]  (уже нормализовано) — используется напрямую;
 *   - user.roles:  ['hub-admin', ...] (строки из Keycloak) — конвертируется;
 *   - user.roles:  [{name}] (объекты) — берётся name/role/value.
 *
 * @param {Object|null} user
 * @returns {Array<{name: string}>}
 */
export const getUserGroups = (user) => {
  if (!user) return [];
  if (Array.isArray(user.groups) && user.groups.length) return user.groups;

  const roles = user.roles;
  if (!Array.isArray(roles)) {
    if (roles !== undefined && roles !== null) {
      logger.warn('[roleUtils] getUserGroups: roles is not an array', roles);
    }
    return [];
  }

  return roles
    .map((role) => {
      if (typeof role === 'string') return { name: role };
      if (role && typeof role === 'object') {
        const name = role.name || role.role || role.value || '';
        return name ? { name } : null;
      }
      logger.warn('[roleUtils] getUserGroups: unknown role format', role);
      return null;
    })
    .filter(Boolean);
};

/**
 * Проверяет наличие хотя бы одной из требуемых ролей.
 * Используется в RequireRole и для фильтрации сайдбара.
 *
 * @param {Array<{name: string}>} userGroups
 * @param {Array<string>|null} requiredRoles - null = без ограничений.
 * @returns {boolean}
 */
export const hasAnyRole = (userGroups, requiredRoles) => {
  if (!requiredRoles) return true;
  if (!userGroups?.length) return false;
  return userGroups.some((g) => requiredRoles.includes(g.name));
};

/** Является ли пользователь администратором хаба. */
export const isHubAdmin = (userGroups) =>
  userGroups?.some((g) => g.name === USER_ROLES.HUB_ADMIN) || false;

/** Является ли пользователь инженером поддержки хаба. */
export const isSupportEngineer = (userGroups) =>
  userGroups?.some((g) => g.name === USER_ROLES.HUB_SUPPORT_ENGINEER) || false;

/** Сотрудник провайдера (hub-admin или hub-support-engineer). */
export const isProviderStaff = (userGroups) =>
  isHubAdmin(userGroups) || isSupportEngineer(userGroups);

/** Администратор организации. */
export const isOrgAdmin = (userGroups) =>
  userGroups?.some((g) => g.name === USER_ROLES.ORG_ADMIN) || false;

/** Участник организации. */
export const isOrgMember = (userGroups) =>
  userGroups?.some((g) => g.name === USER_ROLES.ORG_MEMBER) || false;
